import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useTranslate } from 'react-redux-multilingual';
import { Modal, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faReceipt, faPlus } from '@fortawesome/free-solid-svg-icons';
import Moment from 'react-moment';
import axios from 'axios';
import swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';
import { BlobProvider } from '@react-pdf/renderer';
import { IsAuthContext } from 'app/App';
import Ticket from 'app/views/Pdf/Ticket';
import TicketSupplement from 'app/views/Pdf/TicketSupplement';
import PlanTableCanvas from './PlanTableCanvas';
import PlanTableReservationModal, { ReservationForm } from './PlanTableReservationModal';
import { InputSearchClient } from '@gull';

// ─── Utilities ────────────────────────────────────────────────────────────────

const handleAxiosError = (error, t) => {
    if(error === 'end_session') return;
    if(error.response === undefined){
        swal.fire(t('unknowError'), '', 'error');
    }else if(error.response.status === 403){
        swal.fire(t('forbiddenGeneral'), '', 'error');
    }else{
        swal.fire(t('unknowError'), '', 'error');
    }
};

const nowString = () => {
    const d = new Date();
    return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0')+'T'+String(d.getHours()).padStart(2,'0')+':'+String(d.getMinutes()).padStart(2,'0');
};

const todayDateStr = () => {
    const d = new Date();
    return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');
};

// ─── Modal enfant : réservations d'un client ──────────────────────────────────

const PlanTableClientReservationsModal = ({ show, onHide, clientId, sectionId, tables, onDeleted, onAdded, onUpdated, preloadedClient, onCreateTicket = null, onOpenTicket = null, loadingOpenTicket = false }) => {
    const t = useTranslate();
    const [date, setDate] = useState(todayDateStr());
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingReservation, setEditingReservation] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [addTableId, setAddTableId] = useState('');
    const [validButton, setValidButton] = useState(false);
    const [subModalOpen, setSubModalOpen] = useState(false);
    const [creatingTicketId, setCreatingTicketId] = useState(null);

    const clientName = preloadedClient ? `${preloadedClient.name} ${preloadedClient.firstname || ''}`.trim() : null;

    const reservableTables = tables.filter(tbl => !tbl.is_obstacle);

    const fetchReservations = (fetchDate) => {
        if(!clientId) return;
        setLoading(true);
        axios.post(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation/active', {
            client_id: clientId,
            section_id: sectionId,
            date_debut: fetchDate+'T00:00',
            date_fin: fetchDate+'T23:59',
        }).then((response) => {
            if(!response.data.valid){
                swal.fire(t('unknowError'), '', 'error');
            }else{
                setReservations(response.data.datas);
            }
            setLoading(false);
        }).catch((error) => { setLoading(false); handleAxiosError(error, t); });
    };

    useEffect(() => {
        if(!show) {
            setDate(todayDateStr()); setReservations([]);
            setEditingReservation(null); setShowAddForm(false); setAddTableId('');
            setCreatingTicketId(null);
            return;
        }
        fetchReservations(todayDateStr());
    }, [show, clientId]);

    const handleDelete = (r) => {
        swal.fire({
            title: t('confirmDeleteReservation'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('btnDelete'),
            cancelButtonText: t('btnCancel'),
        }).then((result) => {
            if(!result.isConfirmed) return;
            axios.delete(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation/'+r.id
            ).then(() => {
                setReservations(current => current.filter(res => res.id !== r.id));
                if(onDeleted) onDeleted(r.id);
            }).catch((error) => { handleAxiosError(error, t); });
        });
    };

    const handleAdd = (data) => {
        setValidButton(true);
        axios.post(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation', {
            tbl_id: parseInt(addTableId),
            client_id: data.client_id || clientId || null,
            date_debut: data.date_debut,
            date_fin: data.date_fin || null,
            nb_personne: parseInt(data.nb_personne),
            note: data.note || null,
        }).then((response) => {
            if(!response.data.valid){
                let interms='';
                var keys = Object.keys(response.data.errors);
                for(var i = 0; i < keys.length; i++){
                    interms+=response.data.errors[keys[i]].join(',')+','
                }
                interms=interms.split(',')
                let messageError='';
                interms.forEach(interm=>messageError+=t(interm)+'. ')
                messageError=messageError.replace('undefined','');
                swal.fire(messageError, '', 'error');
            }else{
                setReservations(current => [...current, response.data.data]);
                if(onAdded) onAdded(response.data.data);
                setShowAddForm(false); setAddTableId('');
            }
            setValidButton(false);
        }).catch((error) => { setValidButton(false); handleAxiosError(error, t); });
    };

    const handleEdit = (data) => {
        setValidButton(true);
        axios.put(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation/'+editingReservation.id, {
            tbl_id: editingReservation.tbl_id,
            client_id: data.client_id !== null ? data.client_id : editingReservation.client_id,
            date_debut: data.date_debut,
            date_fin: data.date_fin || null,
            nb_personne: parseInt(data.nb_personne),
            note: data.note || null,
        }).then((response) => {
            if(!response.data.valid){
                let interms='';
                var keys = Object.keys(response.data.errors);
                for(var i = 0; i < keys.length; i++){
                    interms+=response.data.errors[keys[i]].join(',')+','
                }
                interms=interms.split(',')
                let messageError='';
                interms.forEach(interm=>messageError+=t(interm)+'. ')
                messageError=messageError.replace('undefined','');
                swal.fire(messageError, '', 'error');
            }else{
                setReservations(current => current.map(r => r.id === editingReservation.id ? response.data.data : r));
                if(onUpdated) onUpdated(response.data.data);
                setEditingReservation(null);
            }
            setValidButton(false);
        }).catch((error) => { setValidButton(false); handleAxiosError(error, t); });
    };

    const openEdit = (r) => { setShowAddForm(false); setEditingReservation(r); };
    const cancelEdit = () => setEditingReservation(null);

    const handleCreateTicketClick = async (reservationId, clientId) => {
        setCreatingTicketId(reservationId);
        try {
            if (onCreateTicket) await onCreateTicket(reservationId, clientId);
        } finally {
            setCreatingTicketId(null);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered dialogClassName={subModalOpen ? 'modal-low' : undefined}>
            <Modal.Header closeButton>
                <div className="d-flex align-items-center gap-3 me-3">
                    <Modal.Title>{clientName || t('client')} — {t('reservations')}</Modal.Title>
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        style={{ width: 160 }}
                        value={date}
                        onChange={e => setDate(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && fetchReservations(date)}
                    />
                    <button
                        className="btn btn-sm btn-primary flex-shrink-0"
                        onClick={() => fetchReservations(date)}
                        disabled={loading}
                    >
                        {loading ? <span className="spinner-border spinner-border-sm" /> : t('btnValidate')}
                    </button>
                </div>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4"><div className="spinner-border" /></div>
                ) : (
                    <>
                        {reservations.length === 0 && !showAddForm ? (
                            <p className="text-muted text-center py-2">{t('noReservationToday')}</p>
                        ) : (
                            <table className="table table-sm mb-3">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>{t('table')} / {t('zone')}</th>
                                        <th>{t('reservationDateDebut')}</th>
                                        <th>{t('reservationDateFin')}</th>
                                        <th>{t('nbPersons')}</th>
                                        <th>{t('reservationNote')}</th>
                                        <th></th>
                                        {(onCreateTicket || onOpenTicket) && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {reservations.map(r => {
                                        const now = new Date();
                                        const start = new Date(r.date_debut);
                                        const end = r.date_fin ? new Date(r.date_fin) : null;
                                        const status = start > now ? 'upcoming'
                                            : (end === null || end > now) ? 'ongoing'
                                            : 'past';
                                        const rowClass = status === 'ongoing' ? 'table-secondary'
                                            : status === 'upcoming' ? 'table-warning'
                                            : '';
                                        const badgeBg = status === 'ongoing' ? 'secondary'
                                            : status === 'upcoming' ? 'warning'
                                            : 'light';
                                        const badgeLabel = status === 'ongoing' ? t('tableStatusOngoing')
                                            : status === 'past' ? t('tableStatusPast')
                                            : t('tableStatusUpcoming');
                                        const tableObj = tables.find(tbl => tbl.id === r.tbl_id) || r.tbl || null;
                                        return (
                                            <React.Fragment key={r.id}>
                                                <tr className={rowClass}>
                                                    <td>
                                                        <Badge bg={badgeBg} text={badgeBg === 'light' || badgeBg === 'warning' ? 'dark' : undefined} style={{ fontSize: '0.7rem' }}>
                                                            {badgeLabel}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        {t('table')} {tableObj?.numero ?? r.tbl_id}
                                                        {tableObj?.plan_name && <span className="text-muted ms-1">— {tableObj.plan_name}</span>}
                                                    </td>
                                                    <td><Moment format="DD/MM/YYYY HH:mm">{r.date_debut}</Moment></td>
                                                    <td>{r.date_fin ? <Moment format="DD/MM/YYYY HH:mm">{r.date_fin}</Moment> : '—'}</td>
                                                    <td>{r.nb_personne}</td>
                                                    <td>{r.note || '—'}</td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            <button className="btn btn-sm btn-primary" onClick={() => openEdit(r)}>
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r)}>
                                                                <i className="i-Close-Window" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                    {(onCreateTicket || onOpenTicket) && (
                                                        <td>
                                                            {r.ticket && r.ticket.status === 0 && (!r.ticket.commande || r.ticket.commande.cancel === 0) ? (
                                                                <button className="btn btn-sm btn-warning text-nowrap" onClick={() => onOpenTicket && onOpenTicket(r.ticket)}>
                                                                    <FontAwesomeIcon icon={faReceipt} className="me-1" />
                                                                    {t('ticketEnCours')}
                                                                </button>
                                                            ) : r.ticket && r.ticket.status > 0 ? (
                                                                <button className="btn btn-sm btn-primary text-nowrap" onClick={() => onOpenTicket && onOpenTicket(r.ticket)} disabled={loadingOpenTicket}>
                                                                    {loadingOpenTicket ? <span className="spinner-border spinner-border-sm me-1" /> : <FontAwesomeIcon icon={faReceipt} className="me-1" />}
                                                                    {t('openTicketForTable')}
                                                                </button>
                                                            ) : (
                                                                <button className="btn btn-sm btn-success text-nowrap" onClick={() => handleCreateTicketClick(r.id, r.client_id)} disabled={creatingTicketId === r.id}>
                                                                    {creatingTicketId === r.id ? <span className="spinner-border spinner-border-sm me-1" /> : <FontAwesomeIcon icon={faPlus} className="me-1" />}
                                                                    {t('createTicketForTable')}
                                                                </button>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                                {editingReservation?.id === r.id && (
                                                    <tr>
                                                        <td colSpan="7" className="p-3 border-top">
                                                            <h6 className="mb-3">{t('editReservation')}</h6>
                                                            <ReservationForm
                                                                key={r.id}
                                                                table={tableObj}
                                                                onSubmit={handleEdit}
                                                                onCancel={cancelEdit}
                                                                validButton={validButton}
                                                                initialReservation={editingReservation}
                                                                onSubModalChange={setSubModalOpen}
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}

                        {showAddForm ? (
                            <div className="border rounded p-3">
                                <h6 className="mb-3">{t('addReservation')}</h6>
                                <div className="form-group mb-3">
                                    <label className="form-label">{t('table')} <span className="text-danger">*</span></label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={addTableId}
                                        onChange={e => setAddTableId(e.target.value)}
                                    >
                                        <option value="">—</option>
                                        {reservableTables.map(tbl => (
                                            <option key={tbl.id} value={tbl.id}>
                                                {t('table')} {tbl.numero} ({tbl.nb_personne}p)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {addTableId && (
                                    <ReservationForm
                                        table={reservableTables.find(tbl => tbl.id === parseInt(addTableId))}
                                        onSubmit={handleAdd}
                                        onCancel={() => { setShowAddForm(false); setAddTableId(''); }}
                                        validButton={validButton}
                                        defaultClientId={clientId}
                                        preloadedClient={preloadedClient}
                                        onSubModalChange={setSubModalOpen}
                                    />

                                )}
                                {!addTableId && (
                                    <div className="d-flex justify-content-end">
                                        <button className="btn btn-sm btn-danger" onClick={() => { setShowAddForm(false); setAddTableId(''); }}>{t('btnCancel')}</button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            !editingReservation && (
                                <button className="btn btn-sm btn-primary" onClick={() => setShowAddForm(true)}>
                                    + {t('addReservation')}
                                </button>
                            )
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

// ─── Contenu principal : utilisable en modale ou en page ──────────────────────

export const PlanTableContent = ({ sectionId, show = true, isModal = false, onClose, onOuterModalLowChange, canvasMaxHeight, onCreateTicketForTable = null, onOpenTicketForTable = null, currentTicket = null, loadingOpenTicket = false }) => {
    const t = useTranslate();

    // ─── State ────────────────────────────────────────────────────────────────

    const [plans, setPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [reservations, setReservations] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);
    const [searchDatetime, setSearchDatetime] = useState('');
    const [searchNbPersonnes, setSearchNbPersonnes] = useState('');
    // Date/heure réellement prise en compte pour l'affichage (mise à jour uniquement au clic sur "Rechercher")
    const [appliedSearchDatetime, setAppliedSearchDatetime] = useState('');
    const [availableTables, setAvailableTables] = useState(null);
    const [availableDates, setAvailableDates] = useState(null);
    const [searching, setSearching] = useState(false);
    const [loadingInitial, setLoadingInitial] = useState(false);
    const [loadingAllTables, setLoadingAllTables] = useState(false);
    const [filterClientId, setFilterClientId] = useState(null);
    const [filterClientObj, setFilterClientObj] = useState(null);
    const [sectionDefaultClientId, setSectionDefaultClientId] = useState(null);
    const [defaultClientObj, setDefaultClientObj] = useState(null);
    const [defaultReservationTime, setDefaultReservationTime] = useState(null);
    const [showClientReservations, setShowClientReservations] = useState(false);
    const [clientInputKey, setClientInputKey] = useState(0);
    const [viewMode, setViewMode] = useState('canvas');
    const [planningDebut, setPlanningDebut] = useState('');
    const [planningFin, setPlanningFin] = useState('');
    const [planningReservations, setPlanningReservations] = useState([]);
    const [loadingPlanning, setLoadingPlanning] = useState(false);
    const [planningEditingReservation, setPlanningEditingReservation] = useState(null);
    const [planningShowAddForm, setPlanningShowAddForm] = useState(false);
    const [planningAddTableId, setPlanningAddTableId] = useState('');
    const [planningValidButton, setPlanningValidButton] = useState(false);
    const [planningSubModalOpen, setPlanningSubModalOpen] = useState(false);
    const [allTables, setAllTables] = useState([]);
    const [services, setServices] = useState([]);
    const [planningService, setPlanningService] = useState('day');
    const [planningSearchDone, setPlanningSearchDone] = useState(false);
    const [planningCreatingTicketId, setPlanningCreatingTicketId] = useState(null);

    // ─── Derived ──────────────────────────────────────────────────────────────

    const tables = selectedPlanId ? allTables.filter(tbl => Number(tbl.plan_tbl_id) === Number(selectedPlanId)) : [];

    const availableTableIds = (() => {
        if(!availableTables) return null;
        if(allTables.length === 0) return null;
        const apiIds = new Set(availableTables.map(t => Number(t.id)));
        return allTables
            .filter(tbl => !tbl.is_obstacle && apiIds.has(Number(tbl.id)))
            .map(tbl => Number(tbl.id));
    })();

    const availableCountForPlan = (planId) => {
        if(!availableTables || allTables.length === 0) return null;
        const availableIds = new Set(availableTables.map(t => Number(t.id)));
        return allTables.filter(tbl =>
            Number(tbl.plan_tbl_id) === Number(planId) &&
            !tbl.is_obstacle &&
            availableIds.has(Number(tbl.id))
        ).length;
    };

    const reservableTables = tables.filter(tbl => !tbl.is_obstacle);
    const initialDates = availableTableIds !== null && selectedTable && availableTableIds.includes(Number(selectedTable.id))
        ? availableDates
        : null;
    const showCanvas = !!selectedPlanId;
    const selectedPlan = plans.find(p => p.id === selectedPlanId) || null;

    // ─── Notifie le parent Modal quand des sous-modales s'ouvrent/ferment ─────

    useEffect(() => {
        if(onOuterModalLowChange) {
            onOuterModalLowChange(!!(selectedTable || showClientReservations || planningSubModalOpen));
        }
    }, [selectedTable, showClientReservations, planningSubModalOpen]);

    // ─── Effects ──────────────────────────────────────────────────────────────

    // Initialisation à l'ouverture
    useEffect(function() {
        if(!show){
            setPlans([]);
            setSelectedPlanId(null);
            setReservations([]);
            setSearchDatetime('');
            setAppliedSearchDatetime('');
            setSearchNbPersonnes('');
            setAvailableTables(null);
            setAvailableDates(null);
            setLoadingInitial(false);
            setLoadingAllTables(false);
            setFilterClientId(null);
            setShowClientReservations(false);
            setClientInputKey(k => k + 1);
            setViewMode('canvas');
            setPlanningDebut('');
            setPlanningFin('');
            setPlanningReservations([]);
            setPlanningEditingReservation(null);
            setPlanningShowAddForm(false);
            setPlanningAddTableId('');
            setAllTables([]);
            setServices([]);
            setPlanningService('day');
            setPlanningSearchDone(false);
            setPlanningCreatingTicketId(null);
            setFilterClientObj(null);
            setDefaultClientObj(null);
            setSectionDefaultClientId(null);
            setDefaultReservationTime(null);
            return;
        }
        if(!sectionId) return;

        const d0 = new Date(); d0.setHours(0,0,0,0);
        const d1 = new Date(); d1.setHours(23,59,0,0);
        const fmt = (d) => d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0')+'T'+String(d.getHours()).padStart(2,'0')+':'+String(d.getMinutes()).padStart(2,'0');
        setPlanningDebut(fmt(d0));
        setPlanningFin(fmt(d1));

        // Fetch section pour récupérer le client par défaut configuré
        axios.get(process.env.REACT_APP_API_URL+'api/section/'+sectionId)
            .then((response) => {
                const sect = response.data;
                if(sect.default_client) {
                    setSectionDefaultClientId(sect.default_client.id);
                    setDefaultClientObj(sect.default_client);
                }
                if(sect.advanceparams?.default_reservation_time) {
                    setDefaultReservationTime(sect.advanceparams.default_reservation_time);
                }
            })
            .catch((error) => { handleAxiosError(error, t); });

        axios.get(process.env.REACT_APP_API_URL+'api/pos/service_tbl/section/'+sectionId
        ).then((response) => {
            if(!response.data.valid){
                let interms='';
                var keys = Object.keys(response.data.errors);
                for(var i = 0; i < keys.length; i++){
                    interms+=response.data.errors[keys[i]].join(',')+','
                }
                interms=interms.split(',')
                let messageError='';
                interms.forEach(interm=>messageError+=t(interm)+'. ')
                messageError=messageError.replace('undefined','');
                swal.fire(messageError, '', 'error');
            }else{
                setServices(response.data.datas || []);
            }
        }).catch((error) => { handleAxiosError(error, t); });

        const nowStr = nowString();
        setSearchDatetime(nowStr);
        setAppliedSearchDatetime(nowStr);
        setLoadingInitial(true);

        const d = new Date();
        const today = d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');

        axios.post(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation/active', {
            section_id: sectionId,
            date_debut: today+'T00:00',
            date_fin: today+'T23:59',
        }).then((response) => {
            if(!response.data.valid){
                swal.fire(t('unknowError'), '', 'error');
            }else{
                setReservations(response.data.datas);
            }
        }).catch((error) => { handleAxiosError(error, t); })

        Promise.all([
            axios.get(process.env.REACT_APP_API_URL+'api/pos/plan_tbl/section/'+sectionId
            ).then((response) => {
                if(!response.data.valid){
                    swal.fire(t('unknowError'), '', 'error');
                }else{
                    const datas = response.data.datas || [];
                    setPlans(datas);
                    if(datas.length === 1) setSelectedPlanId(datas[0].id);
                }
            }).catch((error) => { handleAxiosError(error, t); }),
            axios.post(process.env.REACT_APP_API_URL+'api/pos/tbl/available', {
                section_id: sectionId,
                datetime: nowStr,
            }).then((response) => {
                if(!response.data.valid){
                    swal.fire(t('unknowError'), '', 'error');
                }else{
                    setAvailableTables(response.data.datas);
                    setAvailableDates({ debut: response.data.date_debut, fin: response.data.date_fin });
                }
            }).catch((error) => { handleAxiosError(error, t); }),
        ]).finally(() => setLoadingInitial(false))
    }, [show, sectionId])

    // Charge toutes les tables à chaque changement de plans
    useEffect(function() {
        if(plans.length === 0){ setAllTables([]); return; }
        setLoadingAllTables(true);
        Promise.all(
            plans.map(plan =>
                axios.get(process.env.REACT_APP_API_URL+'api/pos/plan_tbl/'+plan.id)
                    .then(r => r.data.valid ? (r.data.data?.tbls || []).map(tbl => ({ ...tbl, plan_name: plan.name })) : [])
                    .catch(() => [])
            )
        ).then(results => { setAllTables(results.flat()); setLoadingAllTables(false); });
    }, [plans])

    // Charge le planning à l'activation de la vue planning
    useEffect(() => {
        if(viewMode === 'planning' && planningDebut && planningFin) {
            fetchPlanning(planningDebut, planningFin);
        }
    }, [viewMode]);

    // ─── Handlers ─────────────────────────────────────────────────────────────

    const searchAvailable = (datetime, nbPersonnes, onDone) => {
        setSearching(true);
        const dateStr = datetime.slice(0, 10);
        Promise.all([
            axios.post(process.env.REACT_APP_API_URL+'api/pos/tbl/available', {
                section_id: sectionId,
                datetime,
                ...(nbPersonnes ? { nb_personne: parseInt(nbPersonnes) } : {}),
            }),
            // Recharge aussi les réservations du jour recherché : sinon le nom affiché
            // sur les tables reste celui de la première date consultée à l'ouverture
            axios.post(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation/active', {
                section_id: sectionId,
                date_debut: dateStr+'T00:00',
                date_fin: dateStr+'T23:59',
            }),
        ]).then(([availableResponse, reservationsResponse]) => {
            if(!availableResponse.data.valid || !reservationsResponse.data.valid){
                swal.fire(t('unknowError'), '', 'error');
            }else{
                setAvailableTables(availableResponse.data.datas);
                setAvailableDates({ debut: availableResponse.data.date_debut, fin: availableResponse.data.date_fin });
                setReservations(reservationsResponse.data.datas);
            }
            setSearching(false);
            if(onDone) onDone();
        }).catch((error) => { setSearching(false); handleAxiosError(error, t); })
    }

    const handleSearchAvailable = () => {
        if(!searchDatetime || !sectionId) return;
        setAppliedSearchDatetime(searchDatetime);
        searchAvailable(searchDatetime, searchNbPersonnes);
    }

    const refreshAvailability = () => {
        if(!searchDatetime || !sectionId) return;
        setAppliedSearchDatetime(searchDatetime);
        searchAvailable(searchDatetime, searchNbPersonnes);
    }

    // Bascule vers la vue planning en alignant sa date sur celle du plan de table
    const handleSwitchToPlanning = () => {
        const baseDate = searchDatetime ? searchDatetime.slice(0, 10) : todayDateStr();
        setPlanningDebut(baseDate+'T00:00');
        setPlanningFin(baseDate+'T23:59');
        setPlanningService('day');
        setViewMode('planning');
    }

    const fetchPlanning = (debut, fin) => {
        if(!debut || !fin || !sectionId) return;
        setLoadingPlanning(true);
        axios.post(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation/active', {
            section_id: sectionId,
            date_debut: debut,
            date_fin: fin,
        }).then((response) => {
            if(!response.data.valid){
                swal.fire(t('unknowError'), '', 'error');
            }else{
                setPlanningReservations(response.data.datas);
            }
            setLoadingPlanning(false);
            setPlanningSearchDone(true);
        }).catch((error) => { setLoadingPlanning(false); handleAxiosError(error, t); });
    };

    const handlePlanningAdd = (data) => {
        setPlanningValidButton(true);
        axios.post(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation', {
            tbl_id: parseInt(planningAddTableId),
            client_id: data.client_id || null,
            date_debut: data.date_debut,
            date_fin: data.date_fin || null,
            nb_personne: parseInt(data.nb_personne),
            note: data.note || null,
        }).then((response) => {
            if(!response.data.valid){
                let interms='';
                var keys = Object.keys(response.data.errors);
                for(var i = 0; i < keys.length; i++){
                    interms+=response.data.errors[keys[i]].join(',')+','
                }
                interms=interms.split(',')
                let messageError='';
                interms.forEach(interm=>messageError+=t(interm)+'. ')
                messageError=messageError.replace('undefined','');
                swal.fire(messageError, '', 'error');
            }else{
                const res = response.data.data;
                setPlanningReservations(current => [...current, res]);
                handleReservationAdded(res);
                setPlanningShowAddForm(false); setPlanningAddTableId('');
            }
            setPlanningValidButton(false);
        }).catch((error) => { setPlanningValidButton(false); handleAxiosError(error, t); });
    };

    const handlePlanningEdit = (data) => {
        setPlanningValidButton(true);
        axios.put(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation/'+planningEditingReservation.id, {
            tbl_id: planningEditingReservation.tbl_id,
            client_id: data.client_id !== null ? data.client_id : planningEditingReservation.client_id,
            date_debut: data.date_debut,
            date_fin: data.date_fin || null,
            nb_personne: parseInt(data.nb_personne),
            note: data.note || null,
        }).then((response) => {
            if(!response.data.valid){
                let interms='';
                var keys = Object.keys(response.data.errors);
                for(var i = 0; i < keys.length; i++){
                    interms+=response.data.errors[keys[i]].join(',')+','
                }
                interms=interms.split(',')
                let messageError='';
                interms.forEach(interm=>messageError+=t(interm)+'. ')
                messageError=messageError.replace('undefined','');
                swal.fire(messageError, '', 'error');
            }else{
                const res = response.data.data;
                setPlanningReservations(current => current.map(r => r.id === planningEditingReservation.id ? res : r));
                handleReservationUpdated(res);
                setPlanningEditingReservation(null);
            }
            setPlanningValidButton(false);
        }).catch((error) => { setPlanningValidButton(false); handleAxiosError(error, t); });
    };

    const handlePlanningDelete = (r) => {
        swal.fire({
            title: t('confirmDeleteReservation'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('btnDelete'),
            cancelButtonText: t('btnCancel'),
        }).then((result) => {
            if(!result.isConfirmed) return;
            axios.delete(process.env.REACT_APP_API_URL+'api/pos/tbl_reservation/'+r.id
            ).then(() => {
                setPlanningReservations(current => current.filter(res => res.id !== r.id));
                handleReservationDeleted(r.id);
            }).catch((error) => { handleAxiosError(error, t); });
        });
    };

    const handleReservationAdded = (reservation) => {
        setReservations(current => [...current, reservation]);
        refreshAvailability();
    }

    const handleReservationDeleted = (id) => {
        setReservations(current => current.filter(r => r.id !== id));
        refreshAvailability();
    }

    const handleReservationUpdated = (reservation) => {
        setReservations(current => current.map(r => r.id === reservation.id ? reservation : r));
        refreshAvailability();
    }

    const handleClientSelect = (id) => {
        if(!id) return;
        setFilterClientId(id);
        setShowClientReservations(true);
        axios.get(process.env.REACT_APP_API_URL+'api/client/'+id)
            .then((response) => { setFilterClientObj(response.data); })
            .catch((error) => { handleAxiosError(error, t); });
    };

    const handlePlanningCreateTicketClick = async (reservationId, clientId) => {
        setPlanningCreatingTicketId(reservationId);
        try {
            if (onCreateTicketForTable) await onCreateTicketForTable(reservationId, clientId);
        } finally {
            setPlanningCreatingTicketId(null);
        }
    };

    // ─── Render helpers ───────────────────────────────────────────────────────

    // Contrôles de la barre de header (vue canvas ou planning)
    const headerControls = (
        <div className="d-flex align-items-center gap-3 flex-wrap me-3 w-100">
            {isModal && <Modal.Title className="flex-shrink-0">{t('tablePlan')}</Modal.Title>}
            {isModal && currentTicket && (
                <button className="btn btn-sm btn-success text-nowrap flex-shrink-0" onClick={onClose}>
                    <FontAwesomeIcon icon={faReceipt} className="me-1" />
                    {t('ticketEnCours')}
                </button>
            )}

            <div className="btn-group flex-shrink-0" role="group">
                <button
                    type="button"
                    className={`btn btn-sm ${viewMode === 'canvas' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setViewMode('canvas')}
                >
                    <i className="i-Map-Marker me-1" />{t('tablePlan')}
                </button>
                <button
                    type="button"
                    className={`btn btn-sm ${viewMode === 'planning' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={handleSwitchToPlanning}
                >
                    <i className="i-Calendar me-1" />{t('planningView')}
                </button>
            </div>

            {viewMode === 'canvas' && showCanvas && plans.length > 1 && (
                <div className="d-flex gap-2">
                    {plans.map(plan => {
                        const count = availableCountForPlan(plan.id);
                        return (
                            <button
                                key={plan.id}
                                className={`btn btn-sm ${selectedPlanId === plan.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setSelectedPlanId(plan.id)}
                            >
                                {plan.name}
                                {count !== null && (
                                    <span className={`badge ms-1 ${count > 0 ? 'bg-success' : 'bg-danger'}`}>
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {viewMode === 'canvas' && <div className="d-flex align-items-center gap-2">
                <input
                    type="datetime-local"
                    className="form-control form-control-sm"
                    style={{ width: 210 }}
                    value={searchDatetime}
                    onChange={e => setSearchDatetime(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearchAvailable()}
                />
                <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: 100 }}
                    min="1"
                    placeholder={t('nbPersons')}
                    value={searchNbPersonnes}
                    onChange={e => setSearchNbPersonnes(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearchAvailable()}
                />
                <button
                    className="btn btn-sm btn-primary flex-shrink-0"
                    onClick={handleSearchAvailable}
                    disabled={!searchDatetime || searching}
                >
                    {searching
                        ? <span className="spinner-border spinner-border-sm" />
                        : t('searchAvailability')
                    }
                </button>
                {availableTableIds !== null && (
                    <span className="text-muted small text-nowrap">
                        {availableTableIds.length > 0
                            ? `${availableTableIds.length} ${t('tableStatusAvailable').toLowerCase()}(s)`
                            : t('noAvailableTable')
                        }
                    </span>
                )}
            </div>}

            <div className="ms-auto" style={{ minWidth: 220 }}>
                <InputSearchClient
                    key={clientInputKey}
                    setClient={handleClientSelect}
                />
            </div>
        </div>
    );

    // ─── JSX ──────────────────────────────────────────────────────────────────

    const bodyContent = (
        viewMode === 'planning' ? (
            <div className="p-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        style={{ width: 210 }}
                        value={planningDebut}
                        onChange={e => { setPlanningDebut(e.target.value); setPlanningService(''); }}
                    />
                    <span className="text-muted flex-shrink-0">→</span>
                    <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        style={{ width: 210 }}
                        value={planningFin}
                        onChange={e => { setPlanningFin(e.target.value); setPlanningService(''); }}
                    />
                    <button
                        className="btn btn-sm btn-primary flex-shrink-0"
                        onClick={() => fetchPlanning(planningDebut, planningFin)}
                        disabled={loadingPlanning || !planningDebut || !planningFin}
                    >
                        {loadingPlanning
                            ? <span className="spinner-border spinner-border-sm" />
                            : t('btnValidate')
                        }
                    </button>
                    {(() => {
                        // Raccourci "Journée" toujours proposé en premier, avant les services configurés
                        const periodShortcuts = [
                            { id: 'day', name: t('planningFullDay'), heure_debut: '00:00:00', heure_fin: '23:59:00' },
                            ...services,
                        ];
                        return (
                            <div className="d-flex align-items-center gap-2 ms-3">
                                {periodShortcuts.map(s => (
                                    <button
                                        key={s.id}
                                        type="button"
                                        className={`btn btn-sm rounded-pill ${planningService === String(s.id) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => {
                                            const alreadySelected = planningService === String(s.id);
                                            if(alreadySelected){
                                                setPlanningService('');
                                                return;
                                            }
                                            const dateStr = planningDebut ? planningDebut.slice(0, 10) : todayDateStr();
                                            const debut = `${dateStr}T${s.heure_debut.slice(0, 5)}`;
                                            const fin = `${dateStr}T${s.heure_fin.slice(0, 5)}`;
                                            setPlanningService(String(s.id));
                                            setPlanningDebut(debut);
                                            setPlanningFin(fin);
                                            fetchPlanning(debut, fin);
                                        }}
                                    >
                                        {s.name}
                                        <span className="ms-1 opacity-75" style={{ fontSize: '0.75em' }}>
                                            {s.heure_debut.slice(0, 5)}–{s.heure_fin.slice(0, 5)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        );
                    })()}
                </div>
                {planningSearchDone && (() => {
                    const totalPersons = planningReservations.reduce((sum, r) => sum + (r.nb_personne || 0), 0);
                    const avg = planningReservations.length > 0 ? (totalPersons / planningReservations.length).toFixed(1) : '0';
                    return (
                        <div className="d-flex gap-4 mb-3">
                            <div>
                                <div className="text-muted small">{t('kpiNbReservations')}</div>
                                <div className="fw-bold fs-5">{planningReservations.length}</div>
                            </div>
                            <div>
                                <div className="text-muted small">{t('kpiNbPersons')}</div>
                                <div className="fw-bold fs-5">{totalPersons}</div>
                            </div>
                            <div>
                                <div className="text-muted small">{t('kpiAvgPersons')}</div>
                                <div className="fw-bold fs-5">{avg}</div>
                            </div>
                        </div>
                    );
                })()}
                {loadingPlanning ? (
                    <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
                ) : (
                    <>
                        {planningReservations.length === 0 && !planningShowAddForm ? (
                            <p className="text-muted text-center py-3">{t('noReservationToday')}</p>
                        ) : (
                            <table className="table table-sm mb-3">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>{t('table')} / {t('zone')}</th>
                                        <th>{t('client')}</th>
                                        <th>{t('phone')}</th>
                                        <th>{t('reservationDateDebut')}</th>
                                        <th>{t('reservationDateFin')}</th>
                                        <th>{t('nbPersons')}</th>
                                        <th>{t('reservationNote')}</th>
                                        <th></th>
                                        {(onCreateTicketForTable || onOpenTicketForTable) && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...planningReservations].sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut)).map(r => {
                                        const now = new Date();
                                        const start = new Date(r.date_debut);
                                        const end = r.date_fin ? new Date(r.date_fin) : null;
                                        const status = start > now ? 'upcoming'
                                            : (end === null || end > now) ? 'ongoing'
                                            : 'past';
                                        const rowClass = status === 'ongoing' ? 'table-secondary'
                                            : status === 'upcoming' ? 'table-warning'
                                            : '';
                                        const badgeBg = status === 'ongoing' ? 'secondary'
                                            : status === 'upcoming' ? 'warning'
                                            : 'light';
                                        const badgeLabel = status === 'ongoing' ? t('tableStatusOngoing')
                                            : status === 'past' ? t('tableStatusPast')
                                            : t('tableStatusUpcoming');
                                        const tableObj = allTables.find(tbl => tbl.id === r.tbl_id) || r.tbl || null;
                                        return (
                                            <React.Fragment key={r.id}>
                                                <tr className={rowClass}>
                                                    <td>
                                                        <Badge bg={badgeBg} text={badgeBg === 'light' || badgeBg === 'warning' ? 'dark' : undefined} style={{ fontSize: '0.7rem' }}>
                                                            {badgeLabel}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        {t('table')} {tableObj?.numero ?? r.tbl_id}
                                                        {tableObj?.plan_name && <span className="text-muted ms-1">— {tableObj.plan_name}</span>}
                                                    </td>
                                                    <td>{r.client ? `${r.client.name} ${r.client.firstname || ''}`.trim() : '—'}</td>
                                                    <td>{r.client?.tel || '—'}</td>
                                                    <td><Moment format="DD/MM/YYYY HH:mm">{r.date_debut}</Moment></td>
                                                    <td>{r.date_fin ? <Moment format="DD/MM/YYYY HH:mm">{r.date_fin}</Moment> : '—'}</td>
                                                    <td>{r.nb_personne}</td>
                                                    <td>{r.note || '—'}</td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            <button className="btn btn-sm btn-primary" onClick={() => { setPlanningShowAddForm(false); setPlanningEditingReservation(r); }}>
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button className="btn btn-sm btn-danger" onClick={() => handlePlanningDelete(r)}>
                                                                <i className="i-Close-Window" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                    {(onCreateTicketForTable || onOpenTicketForTable) && (
                                                        <td>
                                                            {r.ticket && r.ticket.status === 0 && (!r.ticket.commande || r.ticket.commande.cancel === 0) ? (
                                                                <button className="btn btn-sm btn-warning text-nowrap" onClick={() => onOpenTicketForTable && onOpenTicketForTable(r.ticket)}>
                                                                    <FontAwesomeIcon icon={faReceipt} className="me-1" />
                                                                    {t('ticketEnCours')}
                                                                </button>
                                                            ) : r.ticket && r.ticket.status > 0 ? (
                                                                <button className="btn btn-sm btn-primary text-nowrap" onClick={() => onOpenTicketForTable && onOpenTicketForTable(r.ticket)} disabled={loadingOpenTicket}>
                                                                    {loadingOpenTicket ? <span className="spinner-border spinner-border-sm me-1" /> : <FontAwesomeIcon icon={faReceipt} className="me-1" />}
                                                                    {t('openTicketForTable')}
                                                                </button>
                                                            ) : (
                                                                <button className="btn btn-sm btn-success text-nowrap" onClick={() => handlePlanningCreateTicketClick(r.id, r.client_id)} disabled={planningCreatingTicketId === r.id}>
                                                                    {planningCreatingTicketId === r.id ? <span className="spinner-border spinner-border-sm me-1" /> : <FontAwesomeIcon icon={faPlus} className="me-1" />}
                                                                    {t('createTicketForTable')}
                                                                </button>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                                {planningEditingReservation?.id === r.id && (
                                                    <tr>
                                                        <td colSpan="9" className="p-3 border-top">
                                                            <h6 className="mb-3">{t('editReservation')}</h6>
                                                            <ReservationForm
                                                                key={r.id}
                                                                table={tableObj}
                                                                onSubmit={handlePlanningEdit}
                                                                onCancel={() => setPlanningEditingReservation(null)}
                                                                validButton={planningValidButton}
                                                                initialReservation={planningEditingReservation}
                                                                onSubModalChange={setPlanningSubModalOpen}
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}

                        {planningShowAddForm ? (
                            <div className="border rounded p-3">
                                <h6 className="mb-3">{t('addReservation')}</h6>
                                <div className="form-group mb-3">
                                    <label className="form-label">{t('table')} <span className="text-danger">*</span></label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={planningAddTableId}
                                        onChange={e => setPlanningAddTableId(e.target.value)}
                                    >
                                        <option value="">—</option>
                                        {allTables.filter(tbl => !tbl.is_obstacle).map(tbl => (
                                            <option key={tbl.id} value={tbl.id}>
                                                {t('table')} {tbl.numero} ({tbl.nb_personne}p)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {planningAddTableId && (
                                    <ReservationForm
                                        table={allTables.find(tbl => tbl.id === parseInt(planningAddTableId))}
                                        onSubmit={handlePlanningAdd}
                                        onCancel={() => { setPlanningShowAddForm(false); setPlanningAddTableId(''); }}
                                        validButton={planningValidButton}
                                        defaultClientId={sectionDefaultClientId}
                                        preloadedClient={defaultClientObj}
                                        onSubModalChange={setPlanningSubModalOpen}
                                    />
                                )}
                                {!planningAddTableId && (
                                    <div className="d-flex justify-content-end">
                                        <button className="btn btn-sm btn-danger" onClick={() => { setPlanningShowAddForm(false); setPlanningAddTableId(''); }}>{t('btnCancel')}</button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            !planningEditingReservation && (
                                <button className="btn btn-sm btn-primary" onClick={() => setPlanningShowAddForm(true)}>
                                    + {t('addReservation')}
                                </button>
                            )
                        )}
                    </>
                )}
            </div>
        ) : loadingInitial ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 220 }}>
                <div className="spinner-border text-primary" />
            </div>
        ) : showCanvas ? (
            <div style={{ position: 'relative' }}>
                <PlanTableCanvas
                    tables={tables}
                    mode="select"
                    reservations={reservations}
                    availableTableIds={availableTableIds}
                    availableDates={availableDates}
                    dimensions={selectedPlan?.dimensions || null}
                    backgroundImage={selectedPlan?.background_image ? process.env.REACT_APP_API_URL+'images/plantbl/'+selectedPlan.background_image : null}
                    maxHeight={canvasMaxHeight || (window.innerHeight - 140)}
                    onSelect={(tbl) => setSelectedTable(tbl)}
                    referenceDatetime={appliedSearchDatetime}
                />
                {(loadingAllTables || searching) && (
                    <div style={{
                        position: 'absolute', inset: 0,
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        background: 'rgba(255,255,255,0.55)',
                        zIndex: 10,
                    }}>
                        <div className="spinner-border text-primary" style={{ width: '2.5rem', height: '2.5rem' }} />
                    </div>
                )}
            </div>
        ) : (
            <div className="d-flex flex-column gap-2 p-3">
                {plans.map(plan => {
                    const count = availableCountForPlan(plan.id);
                    return (
                        <button
                            key={plan.id}
                            className="btn btn-outline-primary text-start py-3 px-4"
                            onClick={() => setSelectedPlanId(plan.id)}
                        >
                            <div className="d-flex align-items-center justify-content-between gap-3">
                                <span className="fw-bold">{plan.name}</span>
                                {count !== null && (
                                    <span className={`badge ${count > 0 ? 'bg-success' : 'bg-danger'}`}>
                                        {count} {t('tableStatusAvailable').toLowerCase()}
                                    </span>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        )
    );

    return (
        <>
            {isModal ? (
                <>
                    <Modal.Header closeButton>
                        {headerControls}
                    </Modal.Header>
                    <Modal.Body style={{ overflow: 'hidden', padding: 0 }}>
                        {bodyContent}
                    </Modal.Body>
                </>
            ) : (
                <>
                    <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
                        {headerControls}
                    </div>
                    <div style={{ overflow: 'hidden' }}>
                        {bodyContent}
                    </div>
                </>
            )}

            <PlanTableClientReservationsModal
                show={showClientReservations}
                onHide={() => { setShowClientReservations(false); setFilterClientObj(null); setClientInputKey(k => k + 1); }}
                clientId={filterClientId}
                sectionId={sectionId}
                tables={allTables}
                preloadedClient={filterClientObj}
                onDeleted={handleReservationDeleted}
                onAdded={handleReservationAdded}
                onUpdated={handleReservationUpdated}
                onCreateTicket={onCreateTicketForTable}
                onOpenTicket={onOpenTicketForTable}
                loadingOpenTicket={loadingOpenTicket}
            />

            <PlanTableReservationModal
                show={!!selectedTable}
                onHide={() => setSelectedTable(null)}
                table={selectedTable}
                initialDates={initialDates}
                defaultClientId={sectionDefaultClientId}
                preloadedClient={defaultClientObj}
                defaultReservationTime={defaultReservationTime}
                onReservationAdded={handleReservationAdded}
                onReservationDeleted={handleReservationDeleted}
                onReservationUpdated={handleReservationUpdated}
                onCreateTicket={onCreateTicketForTable}
                onOpenTicket={onOpenTicketForTable}
                loadingOpenTicket={loadingOpenTicket}
                referenceDatetime={appliedSearchDatetime}
            />
        </>
    );
};

// ─── Wrapper modal ────────────────────────────────────────────────────────────

const PlanTableSelectModal = ({ show, onHide, sectionId }) => {
    const t = useTranslate();
    const { authParam } = useContext(IsAuthContext);
    const [isLow, setIsLow] = useState(false);
    const [cookies, setCookie] = useCookies(['current_ticket']);
    const location = useLocation();

    // ─── PDF ticket finalisé ──────────────────────────────────────────────────

    const btnPdf = useRef('');
    const [ticketBar, setTicketBar] = useState(null);
    const [ticketPdf, setTicketPdf] = useState({});
    const [totalAchat, setTotalAchat] = useState(0);
    const [totalTgc, setTotalTgc] = useState({});
    const [pointsFidelite, setPointsFidelite] = useState({ total: 0, acquis: 0, cashbackTotal: 0, cashbackAcquis: 0, mode: null });
    const [loadingPdf, setLoadingPdf] = useState(false);

    const modulePos = authParam.modules.find(value => value.name === 'pos');
    const supplement_list_params = modulePos?.params?.article?.params?.supplement_list;
    const thisShop = authParam.sections.find(shop => shop.id == sectionId);
    const devise = authParam.company.devise;
    const digit = (authParam.company.advanceparams.hasOwnProperty('ventilation_espece') && authParam.company.advanceparams.ventilation_espece.piece.find(piece => +piece < 1)) ? 2 : 0;

    const tgcForTicketPdf = useMemo(() => {
        const result = {};
        Object.entries(totalTgc).forEach(([tgcName, montant]) => {
            const coef = +tgcName / 100 + 1;
            result[tgcName] = (digit === 0)
                ? +montant - (Math.round(+montant / +coef))
                : Math.round((+montant - (Math.round((+montant / +coef) * 100) / 100)) * 100) / 100;
        });
        return result;
    }, [totalTgc, digit]);
    const tgcTotal = useMemo(() =>
        Object.values(tgcForTicketPdf).reduce((sum, value) => sum + value, 0)
    , [tgcForTicketPdf]);

    // Memoized to keep a stable `document` reference across renders that don't actually change the
    // ticket data: BlobProvider regenerates and revokes its blob URL (URL.revokeObjectURL) every
    // time the `document` reference changes (@react-pdf/renderer usePDF, effect on [doc]). Without
    // this memo, opening the PDF in a new tab produced a blob URL that the next render of this
    // component revoked right away: the preview stayed visible (already loaded by the tab) but
    // Chrome's PDF viewer download icon, which re-fetches that blob URL on click, failed
    // ("Vérifiez votre connexion internet").
    const ticketPdfDocument = useMemo(() => {
        if (Object.keys(ticketPdf).length === 0) return false;
        return supplement_list_params ?
            <TicketSupplement
                ticketPdf={ticketPdf}
                thisShop={thisShop}
                t={t}
                devise={devise}
                digit={digit}
                totalAchat={totalAchat}
                tgcForTicketPdf={tgcForTicketPdf}
                tgcTotal={tgcTotal}
                giftPdf={false}
                pointsFidelite={pointsFidelite}
                moduleParam={modulePos}
                authParam={authParam}
            />
        :
            <Ticket
                ticketPdf={ticketPdf}
                thisShop={thisShop}
                t={t}
                devise={devise}
                digit={digit}
                totalAchat={totalAchat}
                tgcForTicketPdf={tgcForTicketPdf}
                tgcTotal={tgcTotal}
                giftPdf={false}
                pointsFidelite={pointsFidelite}
                moduleParam={modulePos}
                authParam={authParam}
            />
    }, [ticketPdf,thisShop,devise,digit,totalAchat,tgcForTicketPdf,tgcTotal,pointsFidelite,modulePos,authParam,supplement_list_params]);

    // Fetch ticket complet par référence bar puis ouvre le PDF
    useEffect(function() {
        if (!ticketBar) return;
        axios.get(process.env.REACT_APP_API_URL + 'api/pos/ticket/get_by_reference/' + ticketBar)
            .then((response) => {
                if (!response.data.valid) {
                    let interms = '';
                    var keys = Object.keys(response.data.errors);
                    for (var i = 0; i < keys.length; i++) {
                        interms += response.data.errors[keys[i]].join(',') + ',';
                    }
                    interms = interms.split(',');
                    let messageError = '';
                    interms.forEach(interm => messageError += t(interm) + '. ');
                    messageError = messageError.replace('undefined', '');
                    swal.fire(messageError, '', 'error');
                } else {
                    setTicketPdf(response.data.data);
                    setPointsFidelite({
                        total: response.data.total_point_fidelite_client,
                        acquis: response.data.total_point_fidelite_ticket,
                        cashbackTotal: response.data.fidelite_client.valeur_cfp ? response.data.fidelite_client.valeur_cfp : 0,
                        cashbackAcquis: response.data.valeur_cfp_ticket ? response.data.valeur_cfp_ticket : 0,
                        mode: response.data.fidelite_client.mode_fidelite,
                    });
                    var total = 0;
                    let totaltgc = {};
                    response.data.data.ticket_detail.map(item => {
                        total += item.prix_remise;
                        if (item.tgc != 0) {
                            totaltgc = totaltgc[item.tgc]
                                ? { ...totaltgc, [item.tgc]: totaltgc[item.tgc] + item.prix_remise }
                                : { ...totaltgc, [item.tgc]: item.prix_remise };
                        }
                    });
                    setTotalAchat(total);
                    setTotalTgc(totaltgc);
                    setTimeout(() => { btnPdf.current.click(); setTicketBar(null); setLoadingPdf(false); }, 1000);
                }
            })
            .catch((error) => { handleAxiosError(error, t); setLoadingPdf(false); });
    }, [ticketBar]);

    const handleOpenTicket = (ticket) => {
        if (ticket.status > 0) {
            if (loadingPdf) return;
            setLoadingPdf(true);
            setTicketBar(ticket.bar);
        } else {
            const current_date = new Date();
            const expire_date = new Date(current_date.setDate(current_date.getDate() + 90));
            setCookie('current_ticket', ticket.id, { path: '/', expires: expire_date });
            onHide();
        }
    };

    const handleCreateTicket = (reservationId, clientId) => {
        const posid = location.pathname.split('/')[2];
        const idsLocalStorage = localStorage.getItem('pos') ? localStorage.getItem('pos').split(',') : [0, 0];
        const payload = { tbl_reservation_id: reservationId };
        if (clientId) payload.client_id = clientId;
        return axios.post(
            process.env.REACT_APP_API_URL + 'api/pos/ticket/construction/' + posid + '/' + idsLocalStorage[1] + '/' + idsLocalStorage[0],
            payload
        ).then((response) => {
            if (!response.data.valid) {
                let interms = '';
                var keys = Object.keys(response.data.errors);
                for (var i = 0; i < keys.length; i++) {
                    interms += response.data.errors[keys[i]].join(',') + ',';
                }
                interms = interms.split(',');
                let messageError = '';
                interms.forEach(interm => messageError += t(interm) + '. ');
                messageError = messageError.replace('undefined', '');
                swal.fire(messageError, '', 'error');
            } else {
                const current_date = new Date();
                const expire_date = new Date(current_date.setDate(current_date.getDate() + 90));
                setCookie('current_ticket', response.data.data.id, { path: '/', expires: expire_date });
                onHide();
            }
        }).catch((error) => { handleAxiosError(error, t); });
    };

    return (
        <>
            <Modal
                dialogClassName={`modal-95w${isLow ? ' modal-low' : ''}`}
                centered
                show={show}
                onHide={onHide}
            >
                <PlanTableContent
                    sectionId={sectionId}
                    show={show}
                    isModal
                    onClose={onHide}
                    onOuterModalLowChange={setIsLow}
                    onCreateTicketForTable={handleCreateTicket}
                    onOpenTicketForTable={handleOpenTicket}
                    loadingOpenTicket={loadingPdf}
                />
            </Modal>

            <BlobProvider document={ticketPdfDocument}>
                {({ url }) => (
                    <a ref={btnPdf} className="d-none" href={url} target="_blank" rel="noreferrer">Print</a>
                )}
            </BlobProvider>
        </>
    );
};

export default PlanTableSelectModal;
